import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import CustomTable from '../../components/CustomTable'
import { ProgressStat } from './BmsCardPrimitives'

const LIST_COLUMNS = [
  {
    key: 'id',
    label: 'BMS ID',
    width: '30%',
    sortable: true,
    render: (row) => <span className="font-medium text-[#616a79]">{row.id}</span>,
  },
  { key: 'soc', label: 'SoC', render: (row) => `${row.soc ?? '-'}%` },
  { key: 'soh', label: 'SoH', render: (row) => `${row.soh ?? '-'}%` },
  { key: 'packVoltage', label: 'Pack Voltage' },
  { key: 'packCurrent', label: 'Current' },
  {
    key: 'chevron',
    label: '',
    width: '40px',
    align: 'right',
    render: () => <span className="material-icons text-[18px] text-[#616a79] align-middle">chevron_right</span>,
  },
]

const ActiveNode = ({ deviceId, nodes }) => {
  const navigate = useNavigate()
  const [viewMode, setViewMode] = useState('grid')
  const [sortDir, setSortDir] = useState('asc')
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const [searchTerm, setSearchTerm] = useState('')
  const [searchEnabled, setSearchEnabled] = useState(false)
  

  const nodeList = (nodes || []).filter((node) =>
    String(node.id ?? '').toLowerCase().includes(searchTerm.toLowerCase())
  )


  const sortedNodes = [...nodeList].sort((a, b) => {
    const cmp = String(a.id ?? '').localeCompare(String(b.id ?? ''), undefined, { numeric: true })
    return sortDir === 'asc' ? cmp : -cmp
  })
  const totalItems = sortedNodes.length
  const totalPages = Math.ceil(totalItems / itemsPerPage)
  const start = (currentPage - 1) * itemsPerPage
  const pagedNodes = sortedNodes.slice(start, start + itemsPerPage)

  const handleSelectNode = (node) => {
    navigate(`/ViewDashboard/${deviceId}/bms/${node.ecuid}`)
  }

  const handleSearch = () => {
    setSearchEnabled((prev) => !prev)
    if (searchEnabled) {
      setSearchTerm('')
    }
  }

  return (
    <div className="flex-1 flex flex-col gap-3 p-4 overflow-hidden min-w-0">
      <div className="flex items-center justify-between shrink-0 h-10">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-[#252a34]">Active Nodes</span>
          <div className="bg-[#e6f8e6] rounded-full px-2 py-0.5">
            <span className="text-xs font-medium text-[#03791f]">{totalItems}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {searchEnabled && (
            <input
              type="text"
              autoFocus
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value)
                setCurrentPage(1)
              }}
              placeholder="Search BMS ID"
              className="h-9 w-48 px-3 rounded-md border border-[#dfe4ed] bg-[#f9fafa] text-sm text-[#272d37] placeholder:text-[#616a79] outline-none"
            />
          )}
          <button
            className="w-10 h-10 border-none bg-transparent cursor-pointer flex items-center justify-center rounded-lg"
            onClick={handleSearch}
          >
            <span className="material-icons text-xl text-[#272d37]">search</span>
          </button>
          <div className="flex bg-[#dfe4ed] rounded-lg p-0.5 gap-0.5">
            <button
              className={`border-none cursor-pointer px-2.5 py-1 rounded-md flex items-center justify-center ${viewMode === 'list' ? 'bg-[#f9fafa]' : 'bg-transparent'}`}
              onClick={() => setViewMode('list')}
            >
              <span className={`material-icons text-[18px] ${viewMode === 'list' ? 'text-[#272d37]' : 'text-[#616a79]'}`}>list</span>
            </button>
            <button
              className={`border-none cursor-pointer px-2.5 py-1 rounded-md flex items-center justify-center ${viewMode === 'grid' ? 'bg-[#f9fafa]' : 'bg-transparent'}`}
              onClick={() => setViewMode('grid')}
            >
              <span className={`material-icons text-[18px] ${viewMode === 'grid' ? 'text-[#272d37]' : 'text-[#616a79]'}`}>grid_view</span>
            </button>
          </div>
        </div>
      </div>

      {viewMode === 'grid' ? (
        nodeList.length === 0 ? (
          /* The list view says so via CustomTable's emptyMessage; the card grid
             used to just render nothing, which reads as a broken panel. */
          <div className="flex-1 flex flex-col items-center justify-center gap-2 py-10 rounded-lg border border-[#dfe4ed] bg-[#f9fafa]">
            <span className="material-icons text-2xl text-[#c8cfdc]">battery_unknown</span>
            <span className="text-sm font-normal text-[#616a79]">
              {searchTerm.trim() ? 'No nodes match this search' : 'No active nodes reported'}
            </span>
          </div>
        ) : (
          <div className="grid grid-cols-[repeat(auto-fill,minmax(280px,1fr))] gap-3 overflow-y-auto content-start">
            {nodeList.map((node, i) => (
              <BMSCard key={node.ecuid || i} node={node} onClick={() => handleSelectNode(node)} />
            ))}
          </div>
        )
      ) : (
        <CustomTable
          columns={LIST_COLUMNS}
          data={pagedNodes}
          rowKey={(row) => row.ecuid}
          emptyMessage="No active nodes reported"
          bordered
          headerBg="#f2f4f7"
          textColor="#272d37"
          rowBorderColor="#f2f4f7"
          sortColumn="id"
          sortDirection={sortDir}
          onSort={() => {
            setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
            setCurrentPage(1)
          }}
          onRowClick={handleSelectNode}
          pagination={{
            currentPage,
            totalPages,
            totalItems,
            itemsPerPage,
            onPageChange: setCurrentPage,
            onItemsPerPageChange: setItemsPerPage,
          }}
        />
      )}
    </div>
  )
}

const BMSCard = ({ node, onClick }) => (
  <div className="bg-[#f9fafa] rounded-lg p-3 flex flex-col gap-2.5 border border-[#dfe4ed] cursor-pointer" onClick={onClick}>
    <span className="text-sm font-semibold text-[#616a79]">{node.id}</span>
    <div className="flex flex-col gap-2">
      <ProgressStat label="SoC" value={node.soc} type="soc" />
      <ProgressStat label="SoH" value={node.soh} type="soh" />
    </div>
    <div className="flex gap-2">
      <div className="flex-1 bg-[#f2f4f7] rounded-lg px-2.5 py-2 flex flex-col gap-0.5">
        <span className="text-sm font-normal text-[#616a79]">Pack Voltage</span>
        <span className="text-sm font-medium text-[#272d37]">{node.packVoltage}</span>
      </div>
      <div className="flex-1 bg-[#f2f4f7] rounded-lg px-2.5 py-2 flex flex-col gap-0.5">
        <span className="text-sm font-normal text-[#616a79]">Pack Current</span>
        <span className="text-sm font-medium text-[#272d37]">{node.packCurrent}</span>
      </div>
    </div>
  </div>
)

export default ActiveNode
